/**
 * Vet landing template: scroll spy, events fetch + skeletons, ZIP nearest search, review expand.
 * Loaded only on page.vet-landing template (after dental-quiz-core + vet-clinic-zip-search).
 */

(function () {
  'use strict';

  var eventsCache = [];
  var locationMetaCache = {};
  var queryHostHandleFilter = '';
  var queryOverwriteHandleFilter = '';
  var calendarPickerState = { root: null, trigger: null, eventData: null, anchor: null };
  var MIN_PLACEHOLDER_MS = 600;
  var zipIndex = null;
  var zipIndexState = 'idle'; // idle | loading | ready | failed
  var heroEventsGrid = null;
  var lastTrackedZipSignature = '';
  var DEFAULT_PLACEHOLDER_EVENTS = [
    {
      id: 'placeholder-event-1',
      when: 'Loading upcoming clinic time...',
      store: 'Loading clinic information...',
      address: 'Loading address...',
      locationUrl: '/pages/locations',
      handle: '',
      state: '',
      zip: '',
    },
    {
      id: 'placeholder-event-2',
      when: 'Loading upcoming clinic time...',
      store: 'Loading clinic information...',
      address: 'Loading address...',
      locationUrl: '/pages/locations',
      handle: '',
      state: '',
      zip: '',
    },
    {
      id: 'placeholder-event-3',
      when: 'Loading upcoming clinic time...',
      store: 'Loading clinic information...',
      address: 'Loading address...',
      locationUrl: '/pages/locations',
      handle: '',
      state: '',
      zip: '',
    },
  ];

  function normalizeSearch(value) {
    return String(value || '')
      .replace(/\-+/g, ' ')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  function normalizeLocationHandleForLookup(value) {
    return normalizeSearch(value)
      .replace(/^kahoots\s+/, '')
      .replace(/[^a-z0-9\s]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function getHandleLookupKeys(value) {
    var primary = normalizeLocationHandleForLookup(value);
    if (!primary) return [];
    var aliases = {
      'rancho bernardo': ['4s ranch', '4s'],
      '4s ranch': ['rancho bernardo', '4s'],
      '4s': ['rancho bernardo', '4s ranch'],
    };
    return [primary].concat(aliases[primary] || []);
  }

  function toTitleCase(value) {
    return String(value || '')
      .replace(/\-+/g, ' ')
      .toLowerCase()
      .replace(/\b\w/g, function (ch) {
        return ch.toUpperCase();
      });
  }

  function getHandleFromHost(host) {
    if (!host) return '';
    return String(host || '')
      .replace(/\s+/g, '-')
      .toLowerCase();
  }

  function getLocationUrl(host) {
    if (!host) return 'https://kahootsfeedandpet.com/pages/locations';
    var base = '/pages/kahoots-';
    var handle = getHandleFromHost(host);
    switch (handle) {
      case '4s-ranch':
        handle = '4s';
        break;
      default:
        break;
    }
    return base + handle;
  }

  function pad2(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function normalizePhoneForTel(value) {
    return String(value || '').replace(/[^0-9+]/g, '');
  }

  function buildGoogleMapsUrlForEvent(eventData) {
    var text = (eventData && (eventData.address || eventData.fullAddress || eventData.store)) || '';
    return 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(text);
  }

  function getDirectionsUrl(eventData) {
    var raw = String((eventData && eventData.directionsUrl) || '').trim();
    var generatedGoogleMapsUrl = buildGoogleMapsUrlForEvent(eventData);
    if (!raw) return generatedGoogleMapsUrl || '';

    var normalizedRaw = raw;
    if (
      !/^https?:\/\//i.test(normalizedRaw) &&
      /^(www\.|maps\.app\.goo\.gl|goo\.gl\/maps|google\.com\/maps)/i.test(normalizedRaw)
    ) {
      normalizedRaw = 'https://' + normalizedRaw.replace(/^\/+/, '');
    }

    if (/^https?:\/\//i.test(normalizedRaw)) {
      // Replace short-map links with a direct Google Maps query URL so assignedDirectionsUrl is always explicit.
      if (/^https?:\/\/(maps\.app\.goo\.gl|goo\.gl\/maps)/i.test(normalizedRaw)) {
        return generatedGoogleMapsUrl || normalizedRaw;
      }
      return normalizedRaw;
    }

    return generatedGoogleMapsUrl || '';
  }

  function getCallUrl(eventData) {
    var phone = normalizePhoneForTel(eventData && eventData.phone);
    return phone ? 'tel:' + phone : '';
  }

  function hasAnyAction(eventData) {
    return !!(toCalendarRange(eventData) || getDirectionsUrl(eventData) || getCallUrl(eventData));
  }

  function setActionLinkState(anchorEl, href, options) {
    if (!anchorEl) return;
    var isActive = !!href;
    if (isActive) {
      anchorEl.href = href;
      anchorEl.classList.remove('is-disabled');
      anchorEl.removeAttribute('aria-disabled');
      anchorEl.removeAttribute('tabindex');
      if (options && options.target) anchorEl.target = options.target;
      if (options && options.rel) anchorEl.rel = options.rel;
      return;
    }

    anchorEl.removeAttribute('href');
    anchorEl.removeAttribute('target');
    anchorEl.removeAttribute('rel');
    anchorEl.classList.add('is-disabled');
    anchorEl.setAttribute('aria-disabled', 'true');
    anchorEl.setAttribute('tabindex', '-1');
  }

  function parseLocationMetaFromHtml(htmlText) {
    var meta = { phone: '', directionsUrl: '' };
    if (!htmlText) return meta;
    try {
      var parser = new DOMParser();
      var doc = parser.parseFromString(htmlText, 'text/html');
      var phoneAnchor = doc.querySelector('.store-content__contact--phone-link[href^="tel:"]');
      if (!phoneAnchor) phoneAnchor = doc.querySelector('a[href^="tel:"]');
      if (phoneAnchor) {
        meta.phone =
          String(phoneAnchor.textContent || '').trim() ||
          String(phoneAnchor.getAttribute('href') || '').replace(/^tel:/i, '');
      }

      var directionsAnchor = doc.querySelector(
        '.store-content__contact a[href*="google.com/maps"], .store-content__contact a[href*="maps.app.goo.gl"], .store-content__contact a[href*="goo.gl/maps"]',
      );
      if (!directionsAnchor) {
        directionsAnchor = Array.prototype.find.call(
          doc.querySelectorAll('.store-content__contact a[href]'),
          function (el) {
            var txt = String(el.textContent || '').toLowerCase();
            return txt.indexOf('direction') !== -1 || txt.indexOf('map') !== -1;
          },
        );
      }
      if (directionsAnchor) {
        meta.directionsUrl = String(directionsAnchor.getAttribute('href') || '').trim();
      }
    } catch (err) {
      console.warn('Could not parse store page metadata', err);
    }
    return meta;
  }

  function getLiquidLocationMetaByHandle() {
    var raw = window.__VET_LANDING_LOCATION_DATA__;
    var records = [];

    if (Array.isArray(raw)) records = raw;
    else if (raw && Array.isArray(raw.data)) records = raw.data;

    var byHandle = {};
    records.forEach(function (item) {
      var handleKeys = getHandleLookupKeys(item && item.handle);
      if (!handleKeys.length) return;
      var latRaw = item && item.lat;
      var lngRaw = item && item.lng;
      var lat = latRaw == null || latRaw === '' ? null : Number(latRaw);
      var lng = lngRaw == null || lngRaw === '' ? null : Number(lngRaw);
      var meta = {
        phone: String((item && item.phone_number) || '').trim(),
        directionsUrl: String((item && item.direction_link) || '').trim(),
        socialLink: String((item && item.social_link) || '').trim(),
        zip: String((item && item.zip) || '').trim(),
        lat: lat != null && !isNaN(lat) ? lat : null,
        lng: lng != null && !isNaN(lng) ? lng : null,
        handle: String((item && item.handle) || '').trim(),
      };
      handleKeys.forEach(function (handleKey) {
        if (!byHandle[handleKey]) byHandle[handleKey] = meta;
      });
    });

    return byHandle;
  }

  /** Map event handles → store geo for ZIP nearest ranking. */
  function getStoresByHandleForEvents(events) {
    var liquidMetaByHandle = getLiquidLocationMetaByHandle();
    var out = {};
    (events || []).forEach(function (ev) {
      var handle = String((ev && ev.handle) || '').trim();
      if (!handle || out[handle]) return;
      var lookupKeys = getEventLocationLookupKeys(ev);
      var meta = null;
      for (var i = 0; i < lookupKeys.length; i++) {
        if (liquidMetaByHandle[lookupKeys[i]]) {
          meta = liquidMetaByHandle[lookupKeys[i]];
          break;
        }
      }
      out[handle] = {
        handle: handle,
        zip: (meta && meta.zip) || String((ev && ev.zip) || '').trim(),
        lat: meta ? meta.lat : null,
        lng: meta ? meta.lng : null,
      };
    });
    return out;
  }

  function fetchTextWithRetry(url, attemptsLeft, delayMs) {
    return fetch(url, { credentials: 'same-origin' }).then(
      function (response) {
        if (!response.ok) throw new Error('ZIP asset fetch failed: ' + response.status);
        return response.text();
      },
      function (err) {
        throw err;
      },
    ).catch(function (err) {
      if (attemptsLeft <= 1) throw err;
      return new Promise(function (resolve) {
        window.setTimeout(function () {
          resolve(fetchTextWithRetry(url, attemptsLeft - 1, delayMs * 2));
        }, delayMs);
      });
    });
  }

  function loadZipIndex() {
    var Core = window.KdDentalQuizCore;
    var url = window.__VET_LANDING_ZIPS_URL__;
    if (!Core || typeof Core.buildZipIndex !== 'function') {
      zipIndexState = 'failed';
      console.warn('[vet-landing] dental-quiz-core.js missing; ZIP nearest search disabled');
      return Promise.resolve(null);
    }
    if (!url) {
      zipIndexState = 'failed';
      console.warn('[vet-landing] ZIP centroid asset URL missing');
      return Promise.resolve(null);
    }
    if (zipIndexState === 'ready' && zipIndex) return Promise.resolve(zipIndex);
    if (zipIndexState === 'loading') return Promise.resolve(null);

    zipIndexState = 'loading';
    return fetchTextWithRetry(url, 3, 300)
      .then(function (text) {
        zipIndex = Core.buildZipIndex(text || '', {});
        zipIndexState = 'ready';
        if (heroEventsGrid) applyFiltersAndRender(heroEventsGrid);
        return zipIndex;
      })
      .catch(function (err) {
        zipIndexState = 'failed';
        zipIndex = null;
        console.warn('[vet-landing] could not load ZIP centroids; using text search only', err);
        return null;
      });
  }

  function formatDistanceLabel(miles) {
    if (miles == null || !isFinite(miles)) return '';
    if (miles < 1) return 'Under 1 mi';
    return 'About ' + Math.round(miles) + ' mi';
  }

  function updateSearchStatus(result, query) {
    var statusEl = document.getElementById('hero-location-search-status');
    if (!statusEl) return;
    var ZipSearch = window.KdVetClinicZipSearch;
    var trimmed = String(query || '').trim();
    if (!trimmed) {
      statusEl.textContent = '';
      return;
    }
    if (result && result.unplacedZip) {
      statusEl.textContent =
        "We couldn't place that ZIP, so we're showing text matches. Try a California or Arizona ZIP for nearby clinics.";
      return;
    }
    if (result && result.mode === 'distance' && result.events && result.events.length) {
      var nearest = formatDistanceLabel(result.nearestMiles);
      statusEl.textContent =
        'Showing clinics near you' + (nearest ? ' (closest ' + nearest.toLowerCase() + ')' : '') + '.';
      return;
    }
    if (ZipSearch && ZipSearch.isFiveDigitZip(trimmed) && result && result.events && !result.events.length) {
      statusEl.textContent = 'No upcoming clinics near that ZIP.';
      return;
    }
    if (result && result.events && result.events.length) {
      statusEl.textContent = result.events.length + ' clinic' + (result.events.length === 1 ? '' : 's') + ' match your search.';
      return;
    }
    statusEl.textContent = 'No clinics match that search.';
  }

  function publishZipSearchTelemetry(result, query) {
    var ZipSearch = window.KdVetClinicZipSearch;
    if (!ZipSearch || !ZipSearch.isFiveDigitZip(query)) return;
    var signature =
      String(query).trim() +
      '|' +
      (result && result.mode) +
      '|' +
      (result && result.zipMatched) +
      '|' +
      (result && result.events ? result.events.length : 0);
    if (signature === lastTrackedZipSignature) return;
    lastTrackedZipSignature = signature;
    document.dispatchEvent(
      new CustomEvent('vetlanding:zipsearch', {
        detail: {
          zipMatched: !!(result && result.zipMatched),
          resultCount: result && result.events ? result.events.length : 0,
          nearestMiles: result && result.nearestMiles != null ? result.nearestMiles : null,
          mode: result && result.mode,
        },
      }),
    );
  }

  function getEventLocationLookupKeys(eventData) {
    var keys = [];

    function addKey(value) {
      getHandleLookupKeys(value).forEach(function (k) {
        if (k && keys.indexOf(k) === -1) keys.push(k);
      });
    }

    if (!eventData) return keys;

    addKey(eventData.handle);

    var storeText = String(eventData.store || '')
      .replace(/^Kahoots\s*[\-\u2014]\s*/i, '')
      .trim();
    addKey(storeText);

    var locationUrl = String(eventData.locationUrl || '');
    var locationMatch = locationUrl.match(/\/pages\/kahoots-([^/?#]+)/i);
    if (locationMatch && locationMatch[1]) addKey(locationMatch[1]);

    return keys;
  }

  function fetchLocationMetaByUrl(locationUrl) {
    if (!locationUrl) return Promise.resolve({ phone: '', directionsUrl: '' });
    if (locationMetaCache[locationUrl]) {
      return Promise.resolve(locationMetaCache[locationUrl]);
    }

    return fetch(locationUrl, { credentials: 'same-origin' })
      .then(function (response) {
        if (!response.ok) throw new Error('Location page fetch failed: ' + response.status);
        return response.text();
      })
      .then(function (htmlText) {
        var parsed = parseLocationMetaFromHtml(htmlText);
        locationMetaCache[locationUrl] = parsed;
        return parsed;
      })
      .catch(function (err) {
        console.warn('Unable to read location metadata for', locationUrl, err);
        var fallback = { phone: '', directionsUrl: '' };
        locationMetaCache[locationUrl] = fallback;
        return fallback;
      });
  }

  function hydrateLocationMetaForEvents(events) {
    var safeEvents = Array.isArray(events) ? events.slice() : [];
    var liquidMetaByHandle = getLiquidLocationMetaByHandle();
    var mergedFromLiquid = safeEvents.map(function (ev) {
      var lookupKeys = getEventLocationLookupKeys(ev);
      var liquidMeta = {};
      for (var i = 0; i < lookupKeys.length; i++) {
        if (liquidMetaByHandle[lookupKeys[i]]) {
          liquidMeta = liquidMetaByHandle[lookupKeys[i]];
          break;
        }
      }
      var mergedEvent = Object.assign({}, ev, {
        phone: (ev && ev.phone) || liquidMeta.phone || '',
        directionsUrl: getDirectionsUrl(
          Object.assign({}, ev, {
            directionsUrl: (ev && ev.directionsUrl) || liquidMeta.directionsUrl || '',
          }),
        ),
        socialLink: (ev && ev.socialLink) || liquidMeta.socialLink || '',
      });
      return mergedEvent;
    });

    var urls = [];
    mergedFromLiquid.forEach(function (ev) {
      if (!ev || !ev.locationUrl) return;
      if (ev.phone && ev.directionsUrl) return;
      if (urls.indexOf(ev.locationUrl) === -1) urls.push(ev.locationUrl);
    });

    if (!urls.length) return Promise.resolve(mergedFromLiquid);

    return Promise.all(
      urls.map(function (url) {
        return fetchLocationMetaByUrl(url).then(function (meta) {
          return { url: url, meta: meta };
        });
      }),
    ).then(function (results) {
      var byUrl = {};
      results.forEach(function (item) {
        byUrl[item.url] = item.meta || { phone: '', directionsUrl: '' };
      });
      var hydrated = mergedFromLiquid.map(function (ev) {
        var meta = byUrl[ev.locationUrl] || {};
        var mergedEvent = Object.assign({}, ev, {
          phone: ev.phone || meta.phone || '',
          directionsUrl: getDirectionsUrl(
            Object.assign({}, ev, {
              directionsUrl: ev.directionsUrl || meta.directionsUrl || '',
            }),
          ),
        });

        return mergedEvent;
      });
      return hydrated;
    });
  }

  function getLandingPageUrl() {
    try {
      var url = new URL(window.location.href);
      url.hash = '';
      return url.toString();
    } catch (err) {
      return window.location.origin + window.location.pathname;
    }
  }

  function toUtcDateToken(date) {
    var d = new Date(date);
    return (
      d.getUTCFullYear() +
      pad2(d.getUTCMonth() + 1) +
      pad2(d.getUTCDate()) +
      'T' +
      pad2(d.getUTCHours()) +
      pad2(d.getUTCMinutes()) +
      pad2(d.getUTCSeconds()) +
      'Z'
    );
  }

  function toCalendarRange(eventData) {
    var start = new Date(eventData && eventData.start);
    var end = new Date(eventData && eventData.end);
    if (!Number.isFinite(start.getTime()) || !Number.isFinite(end.getTime()) || end <= start) {
      return null;
    }
    return { start: start, end: end };
  }

  function escapeIcsText(value) {
    return String(value || '')
      .replace(/\\/g, '\\\\')
      .replace(/;/g, '\\;')
      .replace(/,/g, '\\,')
      .replace(/\r?\n/g, '\\n');
  }

  function getClinicTitle(eventData) {
    var storeName = String((eventData && eventData.store) || '')
      .replace(/^Kahoots\s*[\-\u2014]\s*/i, '')
      .trim();
    if (!storeName) storeName = 'Store';
    return 'Kahoots Vet Clinic - ' + storeName;
  }

  function getClinicNotes(eventData) {
    var phone = (eventData && eventData.phone) || 'your local store';
    return (
      'Walk in anytime during these hours. Leash your dog, carrier for cats. ' +
      'Vaccines, microchip, nail trim, flea/heartworm, dental (appointment required - call ' +
      phone +
      ').'
    );
  }

  function setVetTrackingData(element, eventData, action) {
    if (!element || !eventData) return;

    element.dataset.vetTrackAction = action || '';
    element.dataset.vetEventId = eventData.id || '';
    element.dataset.vetEventDate = eventData.dateISO || '';
    element.dataset.vetEventTitle = getClinicTitle(eventData);
    element.dataset.vetStoreName = eventData.store || '';
    element.dataset.vetStoreHandle = eventData.handle || '';
    element.dataset.vetStoreCity = eventData.city || '';
    element.dataset.vetStoreState = eventData.state || '';
    element.dataset.vetStoreZip = eventData.zip || '';
    element.dataset.vetStorePhone = eventData.phone || '';
    element.dataset.vetDirectionsUrl = getDirectionsUrl(eventData) || '';
  }

  function buildGoogleCalendarUrl(eventData) {
    var range = toCalendarRange(eventData);
    if (!range) return '';
    var params = new URLSearchParams({
      action: 'TEMPLATE',
      text: getClinicTitle(eventData),
      dates: toUtcDateToken(range.start) + '/' + toUtcDateToken(range.end),
      details: getClinicNotes(eventData),
      location: (eventData && (eventData.address || eventData.fullAddress)) || '',
      sprop: getLandingPageUrl(),
    });
    return 'https://calendar.google.com/calendar/render?' + params.toString();
  }

  function buildIcsText(eventData) {
    var range = toCalendarRange(eventData);
    if (!range) return '';
    var uid =
      'kahoots-vet-' +
      String((eventData && eventData.id) || Date.now()) +
      '-' +
      Math.random().toString(36).slice(2, 10) +
      '@kahootsfeedandpet.com';
    var lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Kahoots//Vet Landing//EN',
      'CALSCALE:GREGORIAN',
      'METHOD:PUBLISH',
      'BEGIN:VEVENT',
      'UID:' + uid,
      'DTSTAMP:' + toUtcDateToken(new Date()),
      'DTSTART:' + toUtcDateToken(range.start),
      'DTEND:' + toUtcDateToken(range.end),
      'SUMMARY:' + escapeIcsText(getClinicTitle(eventData)),
      'DESCRIPTION:' + escapeIcsText(getClinicNotes(eventData)),
      'LOCATION:' + escapeIcsText((eventData && (eventData.address || eventData.fullAddress)) || ''),
      'URL:' + escapeIcsText(getLandingPageUrl()),
      'BEGIN:VALARM',
      'TRIGGER:-P1D',
      'ACTION:DISPLAY',
      'DESCRIPTION:' + escapeIcsText('Kahoots Vet Clinic reminder'),
      'END:VALARM',
      'BEGIN:VALARM',
      'TRIGGER:-PT2H',
      'ACTION:DISPLAY',
      'DESCRIPTION:' + escapeIcsText('Kahoots Vet Clinic reminder'),
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR',
    ];
    return lines.join('\r\n');
  }

  function downloadIcs(eventData) {
    var ics = buildIcsText(eventData);
    if (!ics) return;
    var blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
    var blobUrl = URL.createObjectURL(blob);
    var link = document.createElement('a');
    var safeDate = String((eventData && eventData.dateISO) || '').replace(/[^0-9\-]/g, '');
    var safeStore = String((eventData && eventData.store) || 'clinic')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
    link.href = blobUrl;
    link.download = 'kahoots-vet-clinic-' + (safeStore || 'store') + (safeDate ? '-' + safeDate : '') + '.ics';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(function () {
      URL.revokeObjectURL(blobUrl);
    }, 3000);
  }

  function closeCalendarPicker() {
    var root = calendarPickerState.root;
    if (!root || root.hidden) return;
    root.hidden = true;
    root.classList.remove('is-open');
    root.setAttribute('aria-hidden', 'true');
    root.style.left = '';
    root.style.top = '';
    root.style.visibility = '';
    root.style.pointerEvents = '';
    if (calendarPickerState.activeRow) {
      calendarPickerState.activeRow.classList.remove('is-picker-open');
    }
    if (calendarPickerState.trigger) {
      calendarPickerState.trigger.setAttribute('aria-expanded', 'false');
    }
    calendarPickerState.trigger = null;
    calendarPickerState.eventData = null;
    calendarPickerState.mode = null;
    calendarPickerState.anchor = null;
    calendarPickerState.activeRow = null;
  }

  function getCalendarPickerAnchor(triggerEl) {
    if (!triggerEl) return null;
    return triggerEl.closest('.event-row__actions, .modal-event-row__actions') || triggerEl.parentElement || null;
  }

  function positionCalendarPicker() {
    var root = calendarPickerState.root;
    var triggerEl = calendarPickerState.trigger;
    if (!root || root.hidden || !triggerEl) return;
    if (!document.body.contains(triggerEl)) {
      closeCalendarPicker();
      return;
    }

    var rect = triggerEl.getBoundingClientRect();
    var viewportW = window.innerWidth || document.documentElement.clientWidth || 0;
    var viewportH = window.innerHeight || document.documentElement.clientHeight || 0;

    // If the trigger is no longer on screen, close instead of pinning the picker to a viewport edge.
    if (rect.bottom < 0 || rect.top > viewportH || rect.right < 0 || rect.left > viewportW) {
      closeCalendarPicker();
      return;
    }

    var pickerWidth = Math.max(1, rect.width);
    var left = rect.left;

    root.style.width = pickerWidth + 'px';

    var gap = 6;
    var rootHeight = root.offsetHeight || 160;
    var showAbove = rect.bottom + gap + rootHeight > viewportH - 12 && rect.top - gap - rootHeight > 12;
    var top = showAbove ? rect.top - rootHeight - gap : rect.bottom + gap;

    var maxLeft = Math.max(8, viewportW - pickerWidth - 8);
    left = Math.min(Math.max(8, left), maxLeft);

    root.style.left = Math.round(left) + 'px';
    root.style.top = Math.round(top) + 'px';
    root.classList.toggle('is-anchor-up', showAbove);
  }

  function ensureCalendarPicker() {
    if (calendarPickerState.root) return calendarPickerState.root;
    var root = document.createElement('div');
    root.className = 'calendar-picker';
    root.id = 'vet-calendar-picker';
    root.hidden = true;
    root.setAttribute('aria-hidden', 'true');
    root.innerHTML =
      '<p class="calendar-picker__title">Event actions</p>' +
      '<button type="button" class="calendar-picker__option" data-group="calendar" data-calendar-option="ics">Add to Apple / Outlook</button>' +
      '<button type="button" class="calendar-picker__option" data-group="calendar" data-calendar-option="google">Add to Google Calendar</button>' +
      '<button type="button" class="calendar-picker__option" data-group="info" data-calendar-option="directions">Get directions</button>' +
      '<button type="button" class="calendar-picker__option" data-group="info" data-calendar-option="call">Call store</button>' +
      '<button type="button" class="calendar-picker__cancel" data-calendar-option="cancel">Cancel</button>';
    document.body.appendChild(root);

    window.addEventListener('scroll', positionCalendarPicker, true);
    window.addEventListener('resize', positionCalendarPicker);

    function setOptionState(optionValue, enabled) {
      var btn = root.querySelector('[data-calendar-option="' + optionValue + '"]');
      if (!btn) return;
      btn.disabled = !enabled;
      btn.setAttribute('aria-disabled', enabled ? 'false' : 'true');
      btn.classList.toggle('is-disabled', !enabled);
    }

    function setOptionVisible(optionValue, visible) {
      var btn = root.querySelector('[data-calendar-option="' + optionValue + '"]');
      if (!btn) return;
      btn.hidden = !visible;
    }

    function syncOptionStates(eventData, mode) {
      var hasCalendar = !!toCalendarRange(eventData);

      var showCalendar = mode !== 'info';
      var showInfo = mode !== 'calendar';

      setOptionVisible('ics', showCalendar);
      setOptionVisible('google', showCalendar);
      setOptionVisible('directions', showInfo);
      setOptionVisible('call', showInfo);

      setOptionState('ics', hasCalendar && showCalendar);
      setOptionState('google', hasCalendar && showCalendar);
      setOptionState('directions', !!getDirectionsUrl(eventData) && showInfo);
      setOptionState('call', !!getCallUrl(eventData) && showInfo);
      setOptionState('cancel', true);

      setVetTrackingData(root.querySelector('[data-calendar-option="ics"]'), eventData, 'calendar');
      setVetTrackingData(root.querySelector('[data-calendar-option="google"]'), eventData, 'calendar');
      setVetTrackingData(root.querySelector('[data-calendar-option="directions"]'), eventData, 'directions');
      setVetTrackingData(root.querySelector('[data-calendar-option="call"]'), eventData, 'phone');

      var titleEl = root.querySelector('.calendar-picker__title');
      if (titleEl) {
        if (mode === 'calendar') titleEl.textContent = 'Add to calendar';
        else if (mode === 'info') titleEl.textContent = 'More information';
        else titleEl.textContent = 'Event actions';
      }
    }

    root.addEventListener('click', function (event) {
      var option = event.target.closest('[data-calendar-option]');
      if (!option) return;
      var destination = option.getAttribute('data-calendar-option');
      if (destination === 'cancel') {
        closeCalendarPicker();
        return;
      }
      if (option.disabled) return;
      if (!calendarPickerState.eventData) return;
      if (destination === 'google') {
        var url = buildGoogleCalendarUrl(calendarPickerState.eventData);
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      } else if (destination === 'directions') {
        var directionsUrl = getDirectionsUrl(calendarPickerState.eventData);
        if (directionsUrl) {
          window.open(directionsUrl, '_blank', 'noopener,noreferrer');
        }
      } else if (destination === 'call') {
        var callUrl = getCallUrl(calendarPickerState.eventData);
        if (callUrl) {
          window.location.href = callUrl;
        }
      } else {
        downloadIcs(calendarPickerState.eventData);
      }
      closeCalendarPicker();
    });

    document.addEventListener('mousedown', function (event) {
      if (!calendarPickerState.root || calendarPickerState.root.hidden) return;
      if (calendarPickerState.root.contains(event.target)) return;
      if (calendarPickerState.trigger && calendarPickerState.trigger.contains(event.target)) return;
      closeCalendarPicker();
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') closeCalendarPicker();
    });
    calendarPickerState.root = root;
    calendarPickerState.syncOptionStates = syncOptionStates;
    return root;
  }

  function openCalendarPicker(eventData, triggerEl, mode) {
    if (!triggerEl) return;
    var pickerMode = mode || 'all';
    if (pickerMode === 'calendar' && !toCalendarRange(eventData)) return;
    if (pickerMode === 'info' && !getDirectionsUrl(eventData) && !getCallUrl(eventData)) return;
    if (pickerMode === 'all' && !hasAnyAction(eventData)) return;

    var root = ensureCalendarPicker();
    if (calendarPickerState.trigger === triggerEl && calendarPickerState.mode === pickerMode && !root.hidden) {
      closeCalendarPicker();
      return;
    }

    if (typeof calendarPickerState.syncOptionStates === 'function') {
      calendarPickerState.syncOptionStates(eventData, pickerMode);
    }

    var anchorEl = getCalendarPickerAnchor(triggerEl);
    if (!anchorEl) {
      closeCalendarPicker();
      return;
    }

    if (calendarPickerState.trigger && calendarPickerState.trigger !== triggerEl) {
      calendarPickerState.trigger.setAttribute('aria-expanded', 'false');
    }
    calendarPickerState.trigger = triggerEl;
    calendarPickerState.eventData = eventData;
    calendarPickerState.mode = pickerMode;
    calendarPickerState.anchor = anchorEl;
    if (calendarPickerState.activeRow) {
      calendarPickerState.activeRow.classList.remove('is-picker-open');
    }
    calendarPickerState.activeRow = anchorEl.closest('.event-row, .modal-event-row') || null;
    if (calendarPickerState.activeRow) {
      calendarPickerState.activeRow.classList.add('is-picker-open');
    }
    triggerEl.setAttribute('aria-expanded', 'true');

    root.style.visibility = 'hidden';
    root.style.pointerEvents = 'none';
    root.hidden = false;
    root.classList.add('is-open');
    root.setAttribute('aria-hidden', 'false');
    positionCalendarPicker();
    root.style.visibility = 'visible';
    root.style.pointerEvents = '';
  }

  function getActiveHandleFilter() {
    if (queryOverwriteHandleFilter) return queryOverwriteHandleFilter;
    var handleSelect = document.getElementById('hero-location-filter');
    if (handleSelect && handleSelect.value) return normalizeSearch(handleSelect.value);
    return queryHostHandleFilter;
  }

  function initNavScrollSpy() {
    var navLinks = Array.prototype.slice.call(document.querySelectorAll('.nav-link[data-nav]'));
    var sections = Array.prototype.slice.call(document.querySelectorAll('[data-nav-section]'));
    var navJumpSelect = document.querySelector('[data-nav-jump]');
    var header = document.querySelector('header.site.vet-landing__header');
    var observer = null;
    var visibleRatios = new Map();

    function getHeaderOffset() {
      if (!header) return 88;
      var h = Math.ceil(header.getBoundingClientRect().height || 0);
      return Math.max(h, 64);
    }

    function updateNavOffsetVar() {
      var offset = getHeaderOffset();
      document.documentElement.style.setProperty('--nav-offset', offset + 'px');
      return offset;
    }

    function setActive(navKey) {
      navLinks.forEach(function (a) {
        a.classList.toggle('is-active', a.getAttribute('data-nav') === navKey);
      });
      if (navJumpSelect && navJumpSelect.value !== navKey) {
        navJumpSelect.value = navKey || '';
      }
    }

    function jumpToSection(navKey) {
      if (!navKey) return;
      var target = document.querySelector('[data-nav-section="' + navKey + '"]');
      if (!target) return;
      var headerOffset = updateNavOffsetVar();
      var top = window.pageYOffset + target.getBoundingClientRect().top - headerOffset - 8;
      window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
      setActive(navKey);
    }

    function getNavKeyFromHash() {
      var hash = String(window.location.hash || '').replace(/^#/, '');
      if (!hash) return '';
      var linkMatch = navLinks.find(function (a) {
        var href = String(a.getAttribute('href') || '').replace(/^#/, '');
        return href === hash;
      });
      if (linkMatch) return linkMatch.getAttribute('data-nav') || '';
      return '';
    }

    if (!sections.length) return;

    navLinks.forEach(function (link) {
      link.addEventListener('click', function (event) {
        var navKey = link.getAttribute('data-nav');
        if (!navKey) return;
        event.preventDefault();
        jumpToSection(navKey);
      });
    });

    if (navJumpSelect) {
      navJumpSelect.addEventListener('change', function () {
        jumpToSection(navJumpSelect.value);
      });
    }

    function bindObserver() {
      updateNavOffsetVar();
      if (!('IntersectionObserver' in window)) {
        var hashKey = getNavKeyFromHash();
        if (hashKey) setActive(hashKey);
        else if (sections[0]) setActive(sections[0].getAttribute('data-nav-section') || '');
        return;
      }
      if (observer) observer.disconnect();
      visibleRatios.clear();
      observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (en) {
            var k = en.target.getAttribute('data-nav-section');
            if (!k) return;
            if (en.isIntersecting) visibleRatios.set(k, en.intersectionRatio);
            else visibleRatios.delete(k);
          });
          var bestK = null;
          var bestR = -1;
          visibleRatios.forEach(function (r, k) {
            if (r > bestR) {
              bestR = r;
              bestK = k;
            }
          });
          if (bestK) setActive(bestK);
        },
        {
          root: null,
          rootMargin: '-' + (getHeaderOffset() - 8) + 'px 0px -45% 0px',
          threshold: [0, 0.1, 0.2, 0.35, 0.5, 0.75, 1],
        },
      );
      sections.forEach(function (sec) {
        observer.observe(sec);
      });
    }

    var resizeTimer = null;
    function onResize() {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(bindObserver, 120);
    }

    updateNavOffsetVar();
    var initialHashKey = getNavKeyFromHash();
    if (initialHashKey) setActive(initialHashKey);
    else if (sections[0]) setActive(sections[0].getAttribute('data-nav-section') || '');
    bindObserver();
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', bindObserver);
  }

  /* Function to call manually to mount content with animation, transitioning from display: hidden; useful for things like the hero events that we want to load on demand but not necessarily immediately on page load. */
  const mountFromHidden = (hiddenSelector, mountFn, mountTime) => {
    const hiddenEl = document.querySelector(hiddenSelector);
    if (!hiddenEl) {
      console.warn('Hidden element for mounting not found:', hiddenSelector);
      return;
    }

    // Unhide the element before mounting to allow any CSS animations to run properly
    hiddenEl.style.display = '';
    hiddenEl.classList.remove('mount-hidden');

    // Call the provided mount function to populate the content
    mountFn(hiddenEl, mountTime);
  };

  /* Example usage: mountFromHidden('#vet-landing-events-grid', slideInFromLeft); */
  const mountSlideInFromLeft = (el, time) => {
    el.classList.add('mount-slide-in-from-left');
    setTimeout(() => {
      el.classList.remove('mount-slide-in-from-left');
    }, time || 1000);
  };

  const mountSlideInFromTop = (el, time) => {
    el.classList.add('mount-slide-in-from-top');
    setTimeout(() => {
      el.classList.remove('mount-slide-in-from-top');
    }, time || 1000);
  };

  async function fetchEvents() {
    try {
      const raw = await fetch('/tools/my-kah-api/events/getAll');
      const payload = await raw.json();
      const data = Array.isArray(payload) ? payload : payload && Array.isArray(payload.data) ? payload.data : [];
      const nowMs = Date.now();

      const vetEvents = data
        .filter((event) => {
          if (!event || event.type !== 'Vet Clinic') return false;
          const endMs = new Date(event.end).getTime();
          return Number.isFinite(endMs) && endMs > nowMs;
        })
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
        .map((event) => {
          const fullAddress = String(event.location || '').trim();
          const locationParts = fullAddress
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean);
          const stateZipPart = locationParts.length ? locationParts[locationParts.length - 1] : '';
          const stateZipTokens = stateZipPart.split(/\s+/).filter(Boolean);

          return {
            id: `clinic-${event.id || [event.type, event.host, event.start].join('-')}`,
            tag: event.type || 'Vet Clinic',
            dateISO: new Date(event.start).toISOString().slice(0, 10),
            start: event.start || '',
            end: event.end || '',
            when: new Date(event.start).toLocaleString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            }),
            until: new Date(event.end).toLocaleString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            }),
            fromTo:
              new Date(event.start).toLocaleString('en-US', {
                month: 'short',
                day: 'numeric',
              }) +
              ', ' +
              new Date(event.start).toLocaleString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              }) +
              ' - ' +
              new Date(event.end).toLocaleString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              }),
            store: event.host ? 'Kahoots - ' + event.host : event.name || event.location || '',
            mi: null,
            locationUrl: getLocationUrl(event.host || ''),
            city: locationParts.length >= 2 ? locationParts[locationParts.length - 2] : '',
            address: locationParts.length >= 2 ? locationParts.slice(1).join(', ') : fullAddress,
            fullAddress: fullAddress,
            state: stateZipTokens.length >= 2 ? stateZipTokens[0] : '',
            zip: stateZipTokens.length ? stateZipTokens[stateZipTokens.length - 1] : '',
            phone:
              event.phone || event.phoneNumber || event.storePhone || event.contactPhone || event.locationPhone || '',
            handle: getHandleFromHost(event.host || ''),
          };
        });

      return vetEvents;
    } catch (error) {
      console.error('Error fetching events:', error);
      return Promise.reject(error);
    }
  }

  /** Mock API — swap for real fetch(URL) later. */
  function fetchEventsMock() {
    var rawMock = window.__VET_LANDING_MOCK_EVENTS__;
    var events = [];

    if (typeof rawMock === 'string') {
      try {
        rawMock = JSON.parse(rawMock);
      } catch (err) {
        console.warn('Could not parse window.__VET_LANDING_MOCK_EVENTS__ JSON string:', err);
        rawMock = [];
      }
    }

    if (Array.isArray(rawMock)) {
      events = rawMock;
    } else if (rawMock && Array.isArray(rawMock.data)) {
      events = rawMock.data;
    }

    var delay = typeof window.__VET_LANDING_EVENTS_DELAY_MS === 'number' ? window.__VET_LANDING_EVENTS_DELAY_MS : 900;
    return new Promise(function (resolve) {
      setTimeout(function () {
        resolve(events.slice());
      }, delay);
    });
  }

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function renderEventRow(ev) {
    var row = el('div', 'event-row');
    row.id = ev.id || '';
    row.setAttribute('role', 'listitem');
    setVetTrackingData(row, ev, '');

    const details = el('div', 'event-row__details');
    const whenEl = el('div', 'event-row__line event-row__line--when', escapeHtml(ev.when || ''));
    var storeLabel = ev.store || '';
    var distanceLabel = formatDistanceLabel(ev.mi);
    if (distanceLabel) storeLabel = storeLabel + ' · ' + distanceLabel;
    const storeEl = el('div', 'event-row__line event-row__line--store', escapeHtml(storeLabel));
    const addressText = ev.address || ev.fullAddress || '';
    const addressEl = el('div', 'event-row__line event-row__line--address', escapeHtml(addressText));

    const actions = el('div', 'event-row__actions');

    const calendarBtn = el('button', 'calendar-btn', 'Add to calendar');
    calendarBtn.type = 'button';
    calendarBtn.classList.add('calendar-btn--primary');
    calendarBtn.setAttribute('aria-haspopup', 'dialog');
    calendarBtn.setAttribute('aria-expanded', 'false');
    if (!toCalendarRange(ev)) {
      calendarBtn.disabled = true;
      calendarBtn.title = 'Calendar details unavailable for this event';
    } else {
      calendarBtn.addEventListener('click', function () {
        openCalendarPicker(ev, calendarBtn, 'calendar');
      });
    }

    const moreInfoBtn = el('button', 'calendar-btn calendar-btn--secondary', 'More info');
    moreInfoBtn.type = 'button';
    moreInfoBtn.setAttribute('aria-haspopup', 'dialog');
    moreInfoBtn.setAttribute('aria-expanded', 'false');
    if (!getDirectionsUrl(ev) && !getCallUrl(ev)) {
      moreInfoBtn.disabled = true;
      moreInfoBtn.title = 'Store details unavailable for this event';
    } else {
      moreInfoBtn.addEventListener('click', function () {
        openCalendarPicker(ev, moreInfoBtn, 'info');
      });
    }

    details.appendChild(whenEl);
    details.appendChild(storeEl);
    if (addressText) details.appendChild(addressEl);
    actions.appendChild(calendarBtn);
    actions.appendChild(moreInfoBtn);

    row.appendChild(details);
    row.appendChild(actions);

    return row;
  }

  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function clearSkeletons(container) {
    container.querySelectorAll('.event-row--skeleton').forEach(function (n) {
      n.remove();
    });
  }

  function showSkeletons(container, count) {
    container.innerHTML = '';
    for (var i = 0; i < count; i++) {
      container.appendChild(el('div', 'event-row event-row--skeleton', ''));
    }
  }

  function renderPlaceholderEvents(grid) {
    if (!grid) return;
    grid.innerHTML = '';
    DEFAULT_PLACEHOLDER_EVENTS.forEach(function (ev) {
      var row = renderEventRow(ev);
      row.classList.add('event-row--placeholder');
      grid.appendChild(row);
    });
  }

  function hasSsrEventRows(grid) {
    if (!grid) return false;
    return grid.querySelectorAll('.event-row--ssr').length > 0;
  }

  function parseEventDataFromRow(rowEl) {
    if (!rowEl) return {};
    var d = rowEl.dataset;
    var addressEl = rowEl.querySelector('.event-row__line--address');
    var addressText = addressEl ? String(addressEl.textContent || '').trim() : '';
    return {
      id: d.vetEventId || rowEl.id || '',
      dateISO: d.vetEventDate || '',
      start: d.vetEventStart || '',
      end: d.vetEventEnd || '',
      when:
        (rowEl.querySelector('.event-row__line--when') &&
          String(rowEl.querySelector('.event-row__line--when').textContent || '').trim()) ||
        '',
      store: d.vetStoreName || '',
      handle: d.vetStoreHandle || '',
      city: d.vetStoreCity || '',
      state: d.vetStoreState || '',
      zip: d.vetStoreZip || '',
      phone: d.vetStorePhone || '',
      address: addressText,
      fullAddress: d.vetFullAddress || addressText,
      directionsUrl: d.vetDirectionsUrl || '',
      locationUrl: d.vetLocationUrl || '',
    };
  }

  function bindSsrEventRow(rowEl) {
    if (!rowEl || rowEl.dataset.vetSsrBound === 'true') return;
    rowEl.dataset.vetSsrBound = 'true';
    var ev = parseEventDataFromRow(rowEl);
    var calendarBtn = rowEl.querySelector('.calendar-btn--primary');
    var moreInfoBtn = rowEl.querySelector('.calendar-btn--secondary');

    if (calendarBtn && !calendarBtn.disabled) {
      calendarBtn.addEventListener('click', function () {
        openCalendarPicker(ev, calendarBtn, 'calendar');
      });
    }
    if (moreInfoBtn && !moreInfoBtn.disabled) {
      moreInfoBtn.addEventListener('click', function () {
        openCalendarPicker(ev, moreInfoBtn, 'info');
      });
    }
  }

  function bindAllSsrEventRows(grid) {
    if (!grid) return;
    grid.querySelectorAll('.event-row--ssr').forEach(function (row) {
      bindSsrEventRow(row);
    });
  }

  function clearSsrEmptyMessage(grid) {
    if (!grid) return;
    grid.querySelectorAll('.vet-landing-events-empty--ssr').forEach(function (node) {
      node.remove();
    });
  }

  function publishFilteredEvents(filtered) {
    var safeFiltered = Array.isArray(filtered) ? filtered.slice() : [];
    window.__VET_LANDING_FILTERED_EVENTS__ = safeFiltered;
    document.dispatchEvent(
      new CustomEvent('vetlanding:filterschanged', {
        detail: { events: safeFiltered },
      }),
    );
  }

  function updateStoreChip(selectedHandle) {
    if (!selectedHandle) return;
    var chip = document.querySelector('.store-chip');
    var chipEl = document.getElementById('store-name');
    if (!chip || !chipEl) return;
    chipEl.innerText = 'Upcoming clinics for Kahoots ' + toTitleCase(selectedHandle);
    chip.style.display = '';
    chip.classList.remove('mount-hidden');
    mountSlideInFromLeft(chip, 300);
  }

  function hideStoreChip() {
    var chip = document.querySelector('.store-chip');
    if (!chip) return;
    chip.style.display = 'none';
    chip.classList.add('mount-hidden');
  }

  function filteredEventsMatchHost(filtered) {
    var hostHandle = normalizeSearch(queryHostHandleFilter);
    if (!hostHandle || !Array.isArray(filtered) || !filtered.length) return false;
    return filtered.every(function (ev) {
      return normalizeSearch(ev && ev.handle) === hostHandle;
    });
  }

  function buildSearchText(event) {
    return normalizeSearch([event.store, event.address, event.state, event.zip, event.handle].join(' '));
  }

  function normalizeHandleForMatch(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '');
  }

  function levenshteinDistance(a, b) {
    if (a === b) return 0;
    var aLen = a.length;
    var bLen = b.length;
    if (!aLen) return bLen;
    if (!bLen) return aLen;

    var prev = new Array(bLen + 1);
    var curr = new Array(bLen + 1);
    for (var j = 0; j <= bLen; j++) prev[j] = j;

    for (var i = 1; i <= aLen; i++) {
      curr[0] = i;
      for (var k = 1; k <= bLen; k++) {
        var cost = a.charAt(i - 1) === b.charAt(k - 1) ? 0 : 1;
        curr[k] = Math.min(prev[k] + 1, curr[k - 1] + 1, prev[k - 1] + cost);
      }
      var tmp = prev;
      prev = curr;
      curr = tmp;
    }

    return prev[bLen];
  }

  function handleMatchesFilter(filterHandle, eventHandle) {
    var filterNorm = normalizeHandleForMatch(filterHandle);
    var eventNorm = normalizeHandleForMatch(eventHandle);
    if (!filterNorm || !eventNorm) return false;
    if (filterNorm === eventNorm) return true;
    if (eventNorm.indexOf(filterNorm) !== -1 || filterNorm.indexOf(eventNorm) !== -1) return true;

    var maxDistance = filterNorm.length <= 6 ? 1 : filterNorm.length <= 10 ? 2 : 3;
    return levenshteinDistance(filterNorm, eventNorm) <= maxDistance;
  }

  function hydrateLocationOptions(events) {
    var byHandle = {};
    events.forEach(function (ev) {
      if (!ev.handle || byHandle[ev.handle]) return;
      byHandle[ev.handle] = ev;
    });

    var selectEl = document.getElementById('hero-location-filter');
    if (!selectEl) return;
    Array.prototype.slice.call(selectEl.options).forEach(function (opt) {
      var handle = String(opt.value || '').trim();
      if (!handle) {
        opt.dataset.search = 'all locations';
        return;
      }
      var event = byHandle[handle];
      var name = normalizeSearch(opt.textContent || '').replace(/^kahoots\s+/, '');
      var state = normalizeSearch(event && event.state);
      var zip = normalizeSearch(event && event.zip);
      var address = normalizeSearch(event && event.address);
      opt.dataset.search = normalizeSearch([name, handle, state, zip, address].join(' '));
    });
  }

  function filterLocationOptions(query) {
    var selectEl = document.getElementById('hero-location-filter');
    if (!selectEl) return;
    var normalized = normalizeSearch(query);
    var visibleCount = 0;

    Array.prototype.slice.call(selectEl.options).forEach(function (opt) {
      if (!opt.value) {
        opt.hidden = false;
        return;
      }
      var haystack = opt.dataset.search || '';
      var isMatch = !normalized || haystack.indexOf(normalized) !== -1;
      opt.hidden = !isMatch;
      if (isMatch) visibleCount++;
    });

    if (selectEl.value) {
      var selected = selectEl.querySelector('option[value="' + selectEl.value + '"]');
      if (selected && selected.hidden) selectEl.value = '';
    }

    if (!visibleCount && normalized) {
      selectEl.value = '';
    }
  }

  function getFilteredEvents() {
    var textSearch = document.getElementById('hero-location-search');
    var selectedHandle = getActiveHandleFilter();
    var rawQuery = textSearch ? String(textSearch.value || '') : '';
    var trimmed = rawQuery.trim();
    var hasSearchOverride = Boolean(trimmed);

    var baseEvents = eventsCache.filter(function (ev) {
      if (!hasSearchOverride && selectedHandle && !handleMatchesFilter(selectedHandle, ev.handle)) return false;
      return true;
    });

    var ZipSearch = window.KdVetClinicZipSearch;
    var Core = window.KdDentalQuizCore;

    if (!ZipSearch || typeof ZipSearch.filterClinicEvents !== 'function') {
      var fallbackQuery = normalizeSearch(rawQuery);
      var fallbackEvents = !fallbackQuery
        ? baseEvents.slice()
        : baseEvents.filter(function (ev) {
            return buildSearchText(ev).indexOf(fallbackQuery) !== -1;
          });
      updateSearchStatus(
        { mode: fallbackQuery ? 'substring' : 'empty', events: fallbackEvents, unplacedZip: false },
        rawQuery,
      );
      return fallbackEvents;
    }

    var result = ZipSearch.filterClinicEvents({
      events: baseEvents,
      query: rawQuery,
      zipIndex: zipIndex || {},
      storesByHandle: getStoresByHandleForEvents(baseEvents),
      lookupZip: Core && Core.lookupZip,
      miles: Core && Core.miles,
    });

    updateSearchStatus(result, rawQuery);
    publishZipSearchTelemetry(result, rawQuery);
    return result.events;
  }

  function renderEvents(grid, events) {
    clearSkeletons(grid);
    grid.innerHTML = '';
    var show = events.slice(0, 4);
    if (!show.length) {
      grid.appendChild(el('p', 'vet-landing-events-empty', 'No upcoming clinics at the moment.'));
      return;
    }
    show.forEach(function (ev) {
      var row = renderEventRow(ev);
      grid.appendChild(row);
      mountFromHidden('#' + ev.id, mountSlideInFromTop, 300);
    });
  }

  function applyFiltersAndRender(grid) {
    if (!grid) return;
    var textSearch = document.getElementById('hero-location-search');
    filterLocationOptions(textSearch ? textSearch.value : '');

    var filtered = getFilteredEvents();
    var hasActiveOverwrite = Boolean(queryOverwriteHandleFilter);
    var hasHost = Boolean(queryHostHandleFilter);
    if (hasHost && (!hasActiveOverwrite || filteredEventsMatchHost(filtered))) {
      updateStoreChip(queryHostHandleFilter);
    } else {
      hideStoreChip();
    }
    publishFilteredEvents(filtered);
    renderEvents(grid, filtered);
  }

  function debounce(fn, waitMs) {
    var timeoutId = null;
    return function () {
      var args = arguments;
      var ctx = this;
      if (timeoutId) window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(function () {
        fn.apply(ctx, args);
      }, waitMs);
    };
  }

  function syncOverwriteParam(inputValue) {
    var trimmedValue = String(inputValue || '').trim();
    var overwriteHandleValue = trimmedValue.toLowerCase().replace(/\s+/g, '-').replace(/-+/g, '-');
    var url = new URL(window.location.href);

    if (!overwriteHandleValue) {
      url.searchParams.delete('overwrite');
      queryOverwriteHandleFilter = '';
    } else {
      url.searchParams.set('overwrite', overwriteHandleValue);
      queryOverwriteHandleFilter = normalizeSearch(overwriteHandleValue);
    }

    window.history.replaceState({}, '', url.toString());
  }

  function setSharedSearchFilter(inputValue, source) {
    var value = String(inputValue || '');
    var textSearch = document.getElementById('hero-location-search');
    if (textSearch && textSearch.value !== value) {
      textSearch.value = value;
    }
    syncOverwriteParam(value);
    document.dispatchEvent(
      new CustomEvent('vetlanding:searchsync', {
        detail: {
          source: source || 'hero',
          value: value,
        },
      }),
    );
  }

  function bindEventFilters(grid) {
    var handleSelect = document.getElementById('hero-location-filter');
    var textSearch = document.getElementById('hero-location-search');
    var debouncedApplyFilters = debounce(function () {
      applyFiltersAndRender(grid);
    }, 500);

    if (handleSelect) {
      handleSelect.addEventListener('change', function () {
        applyFiltersAndRender(grid);
      });
    }

    if (textSearch) {
      textSearch.addEventListener('input', function () {
        setSharedSearchFilter(textSearch.value, 'hero');
        debouncedApplyFilters();
      });
    }
  }

  function mountHeroEvents(grid) {
    if (!grid) return;
    heroEventsGrid = grid;
    loadZipIndex();
    var hasSsr = hasSsrEventRows(grid);
    var placeholderStartedAt = Date.now();
    if (hasSsr) {
      bindAllSsrEventRows(grid);
    } else {
      renderPlaceholderEvents(grid);
    }

    fetchEvents()
      .then(function (events) {
        return hydrateLocationMetaForEvents(events);
      })
      .then(function (events) {
        var renderLiveEvents = function () {
          clearSsrEmptyMessage(grid);
          eventsCache = events.slice();
          window.__VET_LANDING_EVENTS__ = events;
          window.__VET_LANDING_GET_FILTERED_EVENTS__ = function () {
            return getFilteredEvents().slice();
          };
          window.__VET_LANDING_SET_SEARCH_FILTER__ = function (value, source) {
            setSharedSearchFilter(value, source || 'external');
            applyFiltersAndRender(grid);
          };

          hydrateLocationOptions(eventsCache);
          bindEventFilters(grid);

          var queryHost = window.URLSearchParams ? new URLSearchParams(window.location.search).get('host') : null;
          var queryOverwrite = window.URLSearchParams
            ? new URLSearchParams(window.location.search).get('overwrite')
            : null;
          queryHostHandleFilter = queryHost ? normalizeSearch(queryHost) : '';
          queryOverwriteHandleFilter = queryOverwrite ? normalizeSearch(queryOverwrite) : '';
          var selectEl = document.getElementById('hero-location-filter');
          if (selectEl) {
            selectEl.value = queryOverwriteHandleFilter || queryHostHandleFilter || '';
          }

          applyFiltersAndRender(grid);

          var evt = new CustomEvent('vetlanding:eventsloaded', { detail: { events: events } });
          document.dispatchEvent(evt);
        };

        var elapsed = Date.now() - placeholderStartedAt;
        var remaining = Math.max(0, MIN_PLACEHOLDER_MS - elapsed);
        if (remaining > 0) {
          window.setTimeout(renderLiveEvents, remaining);
        } else {
          renderLiveEvents();
        }
      })
      .catch(function (err) {
        console.log('Error fetching events:', err);
        if (hasSsr) return;
        grid.innerHTML = '';
        grid.appendChild(el('p', 'vet-landing-events-error', 'Could not load events. Please try again.'));
      });
  }

  function truncateReviewText(text, maxChars) {
    var normalized = String(text || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!normalized || normalized.length <= maxChars) return normalized;
    var slice = normalized.slice(0, maxChars);
    var lastSpace = slice.lastIndexOf(' ');
    if (lastSpace > 40) slice = slice.slice(0, lastSpace);
    return slice.replace(/[\s.,;:!?-]+$/, '') + '...';
  }

  function getReviewTruncateLength(blockquoteEl) {
    if (!blockquoteEl) return 150;
    if (blockquoteEl.classList.contains('review-expandable--in-cta')) return 150;
    if (blockquoteEl.classList.contains('review-expandable--in-hero')) return 150;
    return 170;
  }

  function buildExpandableReviewMarkup(blockquoteEl) {
    if (!blockquoteEl || blockquoteEl.dataset.reviewExpandableReady === 'true') return;

    var fullText = String(blockquoteEl.textContent || '')
      .replace(/\s+/g, ' ')
      .trim();
    if (!fullText) return;

    var maxChars = getReviewTruncateLength(blockquoteEl);
    var previewText = truncateReviewText(fullText, maxChars);
    var needsToggle = previewText !== fullText;

    blockquoteEl.classList.remove('review-expandable--static');
    blockquoteEl.classList.add('review-expandable--subtle');

    var quoteEl = document.createElement('span');
    quoteEl.className = 'review-expandable__quote';

    var peekEl = document.createElement('span');
    peekEl.className = 'review-expandable__peek';
    peekEl.textContent = needsToggle ? previewText : fullText;

    var fullEl = document.createElement('span');
    fullEl.className = 'review-expandable__full';
    fullEl.textContent = fullText;
    fullEl.hidden = true;
    fullEl.setAttribute('aria-hidden', 'true');

    quoteEl.appendChild(peekEl);
    quoteEl.appendChild(fullEl);

    blockquoteEl.innerHTML = '';
    blockquoteEl.appendChild(quoteEl);

    var meta =
      blockquoteEl.parentElement &&
      blockquoteEl.parentElement.querySelector('.review-expandable__meta, .review-tag, .review-meta');

    if (needsToggle) {
      var toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'review-expandable__toggle';
      toggle.setAttribute('aria-expanded', 'false');
      toggle.textContent = 'Read more';
      blockquoteEl.appendChild(toggle);

      peekEl.hidden = false;
      peekEl.setAttribute('aria-hidden', 'false');

      if (meta) {
        meta.hidden = true;
      }
    } else if (meta) {
      meta.hidden = false;
    }

    blockquoteEl.dataset.reviewExpandableReady = 'true';
  }

  function initReviewExpandables() {
    var reviewQuotes = Array.prototype.slice.call(
      document.querySelectorAll(
        'blockquote.review-expandable--static, section#reviews .review-card > blockquote:not(.review-expandable--subtle)',
      ),
    );

    reviewQuotes.forEach(function (quote) {
      if (!quote.classList.contains('review-expandable')) {
        quote.classList.add('review-expandable');
      }
      buildExpandableReviewMarkup(quote);
    });
  }

  function bindReviewExpand() {
    initReviewExpandables();

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.review-expandable__toggle');
      if (!btn) return;
      var wrap = btn.closest('.review-expandable');
      if (!wrap) return;
      var peek = wrap.querySelector('.review-expandable__peek');
      var full = wrap.querySelector('.review-expandable__full');
      if (!peek || !full) return;
      var expanded = btn.getAttribute('aria-expanded') === 'true';
      expanded = !expanded;
      btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      wrap.classList.toggle('is-expanded', expanded);
      peek.hidden = expanded;
      full.hidden = !expanded;
      peek.setAttribute('aria-hidden', expanded ? 'true' : 'false');
      full.setAttribute('aria-hidden', expanded ? 'false' : 'true');
      btn.textContent = expanded ? 'Show less' : 'Read more';
      var meta =
        wrap.parentElement && wrap.parentElement.querySelector('.review-expandable__meta, .review-tag, .review-meta');
      if (meta) meta.hidden = !expanded;
    });
  }

  function replaceHeadingTag(headingEl, tagName) {
    if (!headingEl || !tagName) return null;
    var replacement = document.createElement(tagName);
    Array.prototype.slice.call(headingEl.attributes || []).forEach(function (attr) {
      replacement.setAttribute(attr.name, attr.value);
    });
    replacement.innerHTML = headingEl.innerHTML;
    if (headingEl.parentNode) {
      headingEl.parentNode.replaceChild(replacement, headingEl);
    }
    return replacement;
  }

  function getClosestProductTitle(linkEl) {
    if (!linkEl) return '';
    var root = linkEl.closest('.rebuy-product-block, .rebuy-product') || document;
    var titleEl = root.querySelector(
      '.rebuy-product-title a, .rebuy-product-title, h1 a, h2 a, h3 a, h4 a, h5 a, h1, h2, h3, h4, h5',
    );
    return titleEl ? String(titleEl.textContent || '').trim() : '';
  }

  function patchDynamicAccessibility(root) {
    var scope = root && root.querySelectorAll ? root : document;

    var productLinks = Array.prototype.slice.call(scope.querySelectorAll('a.rebuy-product-image.clickable'));
    productLinks.forEach(function (linkEl) {
      if (linkEl.getAttribute('aria-label')) return;
      var productTitle = getClosestProductTitle(linkEl);
      if (!productTitle) {
        var img = linkEl.querySelector('img[alt]');
        productTitle = img ? String(img.getAttribute('alt') || '').trim() : '';
      }
      if (productTitle) {
        linkEl.setAttribute('aria-label', 'View product: ' + productTitle);
      }
    });

    var cartHeadings = Array.prototype.slice.call(scope.querySelectorAll('.rebuy-cart h4, .cart-drawer h4'));
    cartHeadings.forEach(function (heading) {
      if (heading.dataset.a11yNormalized === 'true') return;
      var text = String(heading.textContent || '').toLowerCase();
      if (text.indexOf('your cart is empty') === -1 && text.indexOf('pet favorites') === -1) return;
      var updated = replaceHeadingTag(heading, 'h3');
      if (updated) updated.dataset.a11yNormalized = 'true';
    });
  }

  function initDynamicAccessibilityPatches() {
    patchDynamicAccessibility(document);
    if (!('MutationObserver' in window)) return;
    var observer = new MutationObserver(function (mutations) {
      mutations.forEach(function (mutation) {
        Array.prototype.slice.call(mutation.addedNodes || []).forEach(function (node) {
          if (!node || node.nodeType !== 1) return;
          patchDynamicAccessibility(node);
        });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function init() {
    document.body.classList.add('vet-landing');
    window.__VET_LANDING_CALENDAR__ = {
      openPicker: openCalendarPicker,
      downloadIcs: downloadIcs,
      getGoogleUrl: buildGoogleCalendarUrl,
      getDirectionsUrl: getDirectionsUrl,
      getCallUrl: getCallUrl,
      hasAnyAction: hasAnyAction,
      hasCalendarData: function (eventData) {
        return !!toCalendarRange(eventData);
      },
    };
    initNavScrollSpy();
    bindReviewExpand();
    initDynamicAccessibilityPatches();
    var grid = document.getElementById('vet-landing-events-grid');
    if (grid) mountHeroEvents(grid);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
